import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AssignmentData } from 'modules/assignments/types';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { AssignmentStatus } from 'modules/status-badge';
import { apiErrorMessage } from '../hooks';
import {
  useCancelHoursRequestMutation,
  useCreateHoursRequestMutation
} from '../query';
import { HoursRequestStatus, SemesterBreak } from '../types';
import { formatMinutesAsHours } from '../utils';

const STATUS_CHIP: Record<
  HoursRequestStatus,
  { color: 'default' | 'info' | 'success' | 'error'; icon: string }
> = {
  REQUESTED: { color: 'info', icon: '⏳' },
  APPROVED: { color: 'success', icon: '✓' },
  DECLINED: { color: 'error', icon: '✗' },
  CANCELLED: { color: 'default', icon: '–' }
};

interface Props {
  semesterBreak: SemesterBreak;
  assignments: AssignmentData[];
  onChanged: (message: string) => void;
}

/**
 * "Work more during this break?" — one row per active assignment. The
 * availability pattern applies immediately; a raised weekly-hours value is an
 * assignment change and needs operator approval. APPROVED is final: it cannot
 * be cancelled here, corrections go through Zenjob.
 */
export const HoursRequestSection = ({
  semesterBreak,
  assignments,
  onChanged
}: Props) => {
  const [hoursByAssignment, setHoursByAssignment] = useState<
    Record<string, string>
  >({});
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateHoursRequestMutation();
  const cancelMutation = useCancelHoursRequestMutation();

  const activeAssignments = assignments.filter(
    assignment => assignment.status === AssignmentStatus.ACTIVE
  );

  if (!activeAssignments.length) return null;

  const requestFor = (assignmentRefId: string) => {
    const requests = semesterBreak.hoursRequests.filter(
      request => request.assignmentRefId === assignmentRefId
    );
    return (
      requests.find(request =>
        ['REQUESTED', 'APPROVED'].includes(request.status)
      ) ?? requests.find(request => request.status === 'DECLINED')
    );
  };

  const submit = (assignmentRefId: string) => {
    const hours = Number(hoursByAssignment[assignmentRefId]);
    if (!hours || hours <= 0 || hours > 60) {
      setError(getTrans('availabilityPage.validation.hours.invalid'));
      return;
    }
    setError(null);
    createMutation.mutate(
      {
        breakRefId: semesterBreak.refId,
        body: {
          assignmentRefId,
          requestedWeeklyMinutes: Math.round(hours * 60)
        }
      },
      {
        onSuccess: () =>
          onChanged(getTrans('availabilityPage.hours.requestedToast')),
        onError: responseError => setError(apiErrorMessage(responseError))
      }
    );
  };

  const cancel = (requestRefId: string) => {
    setError(null);
    cancelMutation.mutate(
      { breakRefId: semesterBreak.refId, requestRefId },
      {
        onSuccess: () =>
          onChanged(getTrans('availabilityPage.hours.cancelledToast')),
        onError: responseError => setError(apiErrorMessage(responseError))
      }
    );
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {getTrans('availabilityPage.hours.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {getTrans('availabilityPage.hours.hint')}
      </Typography>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          data-testid="hours-request-error"
        >
          {error}
        </Alert>
      )}
      {activeAssignments.map(assignment => {
        const request = requestFor(assignment.assignmentRefId);

        return (
          <Box
            key={assignment.assignmentRefId}
            sx={{ mb: 1.5 }}
            data-testid={`hours-request-${assignment.assignmentRefId}`}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {assignment.company} · {assignment.occupation}
            </Typography>

            {request && request.status !== 'DECLINED' ? (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
              >
                <Chip
                  size="small"
                  color={STATUS_CHIP[request.status].color}
                  label={`${STATUS_CHIP[request.status].icon} ${getTrans(
                    request.status === 'REQUESTED'
                      ? 'availabilityPage.hours.status.requested'
                      : 'availabilityPage.hours.status.approved'
                  )} · ${formatMinutesAsHours(
                    request.requestedWeeklyMinutes
                  )} h`}
                />
                {request.status === 'REQUESTED' && (
                  <Button
                    size="small"
                    disabled={cancelMutation.isLoading}
                    onClick={() => cancel(request.refId)}
                    data-testid="cancel-hours-request"
                  >
                    {getTrans('general.cancel')}
                  </Button>
                )}
                {request.status === 'APPROVED' && (
                  <Typography variant="caption" color="text.secondary">
                    {getTrans('availabilityPage.hours.approvedFinal')}
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                {request?.status === 'DECLINED' && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {getTransWithParams('availabilityPage.hours.declined', {
                      comment: request.decisionComment ?? ''
                    })}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 0.5
                  }}
                >
                  <TextField
                    type="number"
                    size="small"
                    sx={{ width: 110 }}
                    label={getTrans('availabilityPage.hours.inputLabel')}
                    value={hoursByAssignment[assignment.assignmentRefId] ?? ''}
                    onChange={event =>
                      setHoursByAssignment({
                        ...hoursByAssignment,
                        [assignment.assignmentRefId]: event.target.value
                      })
                    }
                    inputProps={{
                      min: 1,
                      max: 60,
                      'data-testid': 'hours-request-input'
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={createMutation.isLoading}
                    onClick={() => submit(assignment.assignmentRefId)}
                    data-testid="submit-hours-request"
                  >
                    {getTrans('availabilityPage.hours.submit')}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
